<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    :title="title"
    data-e2e="dialog-disable-client"
    @hide="onDialogHide"
  >
    <div class="row q-col-gutter-md">
      <p
        class="col-12 q-ma-none"
        v-html="message"
      />
      <p class="col-12 q-ma-none">
        {{ additionalText }}
        <strong v-if="target">{{ target }}</strong>
      </p>
      <hr-text-field
        v-if="target"
        v-model="inputValue"
        :label="t('common.inputStrictConfirmLabel', { target })"
        :rules="[(val) => val === target]"
        lazy-rules
        autofocus
        clearable
        hide-bottom-space
        class="col-12"
        data-e2e="input-confirm-client-name"
      />
      <div
        v-if="status === 'active'"
        class="col-12"
      >
        <hr-warning-banner data-e2e="banner-client-license">
          <span v-html="bannerMessage" />
          <template #action>
            <hr-dismiss-btn
              :label="bannerLinkMessage"
              :href="learnMoreUrl"
              target="_blank"
            />
          </template>
        </hr-warning-banner>
      </div>
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="buttonCancelLabel"
        data-e2e="button-cancel-disable-client"
        @click="hide"
      />
      <hr-confirm-btn
        v-if="buttonOkColor === 'primary'"
        :label="buttonOkLabel"
        :disable="!isCompletedRight"
        data-e2e="button-ok-disable-client"
        @click="onOkClick"
      />
      <hr-destructive-btn
        v-else
        :label="buttonOkLabel"
        :disable="!isCompletedRight"
        data-e2e="button-ok-disable-client"
        @click="onOkClick"
      />
    </template>
  </hr-form-dialog>
</template>

<script setup>
import {
  HrConfirmBtn,
  HrDestructiveBtn,
  HrDismissBtn,
  HrFormDialog,
  HrTextField,
  HrWarningBanner,
} from 'src/design-system';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const emit = defineEmits(['hide', 'ok']);

const props = defineProps({
  target: {
    type: String,
    default: null,
  },
  status: {
    type: String,
    default: 'active',
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  additionalText: {
    type: String,
    default: '',
  },
  bannerMessage: {
    type: String,
    default: '',
  },
  bannerLinkMessage: {
    type: String,
    default: '',
  },
  cancelLabel: {
    type: String,
    default: null,
  },
  okLabel: {
    type: String,
    default: null,
  },
  learnMoreUrl: {
    type: String,
    default: 'https://help.hirasmus.com/knowledge/understanding-the-subscription-page',
  },
});

const { t } = useI18n();

const dialog = ref(null);
const inputValue = ref('');

const isCompletedRight = computed(() => {
  if (!props.target) {
    return true;
  }

  return inputValue.value === props.target;
});

const buttonCancelLabel = computed(() => props.cancelLabel || t('common.buttonCancel'));

const buttonOkLabel = computed(() => props.okLabel || t('common.buttonOk'));

const buttonOkColor = computed(() => (props.target ? 'secondary' : 'primary'));

// following method is REQUIRED
// (don't change its name --> "show")
const show = () => {
  inputValue.value = '';
  dialog.value.show();
};

// following method is REQUIRED
// (don't change its name --> "hide")
const hide = () => {
  dialog.value?.hide();
};

const onDialogHide = () => {
  emit('hide');
};

const onOkClick = () => {
  emit('ok');
  hide();
};

defineExpose({ show, hide });
</script>
